import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useI18n } from "@/i18n";
import { Menu, X, ShieldCheck, Phone, Globe } from "lucide-react";

const Header = () => {
  const { t, lang } = useI18n();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  const base = `/${lang}`;

  // Switch language while preserving equivalent page
  const switchLang = (target) => {
    if (target === lang) return;
    const path = location.pathname;
    // If /thank_you => keep it (Spanish only page)
    if (path.startsWith("/thank_you")) return `/thank_you`;
    // map slugs
    const map = {
      mx: {
        privacy: "privacidad",
        cookies: "cookies",
        terms: "terminos",
      },
      en: {
        privacy: "privacy",
        cookies: "cookies",
        terms: "terms",
      },
    };
    if (path.match(/\/(mx|en)\/(privacidad|privacy)/))
      return `/${target}/${map[target].privacy}`;
    if (path.match(/\/(mx|en)\/(cookies)/))
      return `/${target}/${map[target].cookies}`;
    if (path.match(/\/(mx|en)\/(terminos|terms)/))
      return `/${target}/${map[target].terms}`;
    return `/${target}`;
  };

  const navItems = [
    { key: "support", href: `${base}#apoyos` },
    { key: "education", href: `${base}#educacion` },
    { key: "consultation", href: `${base}#consulta` },
    { key: "contact", href: `${base}#contacto` },
  ];

  return (
    <header className="bg-white border-b border-slate-200" data-testid="site-header">
      {/* Official top bar */}
      <div className="bg-[#0B2545] text-white text-xs sm:text-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-9 flex items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <span className="inline-block w-5 h-3.5 rounded-sm overflow-hidden shrink-0" aria-hidden="true">
              <span className="block w-1/3 h-full float-left bg-[#006341]" />
              <span className="block w-1/3 h-full float-left bg-white" />
              <span className="block w-1/3 h-full float-left bg-[#CE1126]" />
            </span>
            <span className="font-medium tracking-wide">{t("header.official_bar")}</span>
          </div>
          <a
            href={`tel:+52${t("header.phone_value").replace(/\s/g, "")}`}
            className="hidden sm:inline-flex items-center gap-2 hover:text-[#D97706]"
            data-testid="header-phone"
          >
            <Phone className="w-3.5 h-3.5" />
            <span>{t("header.phone_label")}: {t("header.phone_value")}</span>
          </a>
        </div>
      </div>

      {/* Main nav */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
        <Link to={base} data-testid="header-logo" className="flex items-center gap-3 shrink-0">
          <span className="w-11 h-11 rounded-md bg-[#0B2545] flex items-center justify-center border-2 border-[#D97706]">
            <ShieldCheck className="w-6 h-6 text-white" />
          </span>
          <span className="flex flex-col leading-tight">
            <span className="font-serif text-xl font-bold text-[#0B2545]">Fundelarento</span>
            <span className="text-xs text-[#475569]">{t("brand.tagline")}</span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-7" data-testid="desktop-nav">
          {navItems.map((it) => (
            <a
              key={it.key}
              href={it.href}
              className="text-sm font-medium text-[#0F172A] hover:text-[#0B2545]"
              data-testid={`nav-${it.key}`}
            >
              {t(`header.nav.${it.key}`)}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <div className="hidden sm:flex items-center gap-1 border border-slate-300 rounded-md p-0.5" data-testid="language-switcher">
            <Link
              to={switchLang("mx")}
              className={`px-2.5 py-1 text-xs font-semibold rounded ${lang === "mx" ? "bg-[#0B2545] text-white" : "text-[#0B2545]"}`}
              data-testid="lang-mx"
              data-language-switch="true"
            >
              ES
            </Link>
            <Link
              to={switchLang("en")}
              className={`px-2.5 py-1 text-xs font-semibold rounded ${lang === "en" ? "bg-[#0B2545] text-white" : "text-[#0B2545]"}`}
              data-testid="lang-en"
              data-language-switch="true"
            >
              EN
            </Link>
          </div>

          <a
            href={`${base}#newsletter`}
            className="hidden md:inline-flex items-center px-4 py-2 text-sm font-semibold bg-[#134E4A] text-white rounded-md hover:bg-[#0f3f3c]"
            data-testid="header-cta"
          >
            {t("header.cta")}
          </a>

          <button
            className="lg:hidden inline-flex items-center justify-center w-11 h-11 rounded-md border border-slate-300 text-[#0B2545]"
            aria-label={open ? t("header.menu_close") : t("header.menu_open")}
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
            data-testid="mobile-menu-button"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="lg:hidden border-t border-slate-200 bg-white" data-testid="mobile-menu-drawer">
          <div className="px-4 py-4 flex flex-col gap-2">
            {navItems.map((it) => (
              <a
                key={it.key}
                href={it.href}
                onClick={() => setOpen(false)}
                className="px-3 py-3 text-base font-medium text-[#0F172A] rounded-md hover:bg-slate-50"
                data-testid={`mobile-nav-${it.key}`}
              >
                {t(`header.nav.${it.key}`)}
              </a>
            ))}
            <a
              href={`${base}#newsletter`}
              onClick={() => setOpen(false)}
              className="mt-1 px-4 py-3 text-center text-sm font-semibold bg-[#134E4A] text-white rounded-md"
              data-testid="mobile-cta"
            >
              {t("header.cta")}
            </a>
            <div className="mt-2 flex items-center gap-2 border-t border-slate-200 pt-3">
              <Globe className="w-4 h-4 text-[#0B2545]" />
              <span className="text-xs text-[#475569]">{t("header.language")}:</span>
              <Link
                to={switchLang("mx")}
                onClick={() => setOpen(false)}
                className={`px-2.5 py-1 text-xs font-semibold rounded border ${lang === "mx" ? "bg-[#0B2545] text-white border-[#0B2545]" : "border-slate-300 text-[#0B2545]"}`}
                data-testid="mobile-lang-mx"
                data-language-switch="true"
              >
                ES
              </Link>
              <Link
                to={switchLang("en")}
                onClick={() => setOpen(false)}
                className={`px-2.5 py-1 text-xs font-semibold rounded border ${lang === "en" ? "bg-[#0B2545] text-white border-[#0B2545]" : "border-slate-300 text-[#0B2545]"}`}
                data-testid="mobile-lang-en"
                data-language-switch="true"
              >
                EN
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
